import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import {
  DetachedRouteHandle,
  RouteReuseStrategy,
  Router,
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import 'dayjs/locale/de';
import 'dayjs/locale/en-au';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-ie';
import 'dayjs/locale/en-nz';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/pl';
import 'dayjs/locale/sr';
import 'dayjs/locale/tr';

import 'moment/locale/de';
import 'moment/locale/en-au';
import 'moment/locale/en-ca';
import 'moment/locale/en-gb';
import 'moment/locale/en-ie';
import 'moment/locale/en-nz';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/fr-ca';
import 'moment/locale/it';
import 'moment/locale/ja';
import 'moment/locale/ko';
import 'moment/locale/pl';
import 'moment/locale/sr';
import 'moment/locale/tr';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideEffects } from '@ngrx/effects';
import { ActionReducer, Store, provideStore } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NGX_EDITOR_CONFIG_TOKEN, NgxEditorModule } from 'ngx-editor';
import { Observable, filter, startWith, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IWidgetGlobalData, WIDGET_GLOBAL_DATA } from 'scw-digital-boards-widgets';
import { routes } from './app.routes';
import { CustomMatPaginatorIntl } from './core/classes/custom-mat-pagination-intl';
import { environment } from './core/environments/environment';
import { GlobalErrorHandlerService } from './core/error-handler/global-error-handler.service';
import { ApiInterceptor, actionTrackerContext, oeeContext } from './core/interceptors/api-interceptor.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { screenLockInterceptor } from './core/interceptors/screen-lock.interceptor';
import { UsersStore } from './pages/settings/users-teams/users/users.store';
import { AdvancedFilterManager } from './shared/components/advanced-filter/advanced-filter-manager';
import { TextEditorComponent } from './shared/components/text-editor/text-editor.component';
import { clientInfoActions } from './state/client-info/client-info.actions';
import { ClientInfoEffects } from './state/client-info/client-info.effects';
import { clientInfoReducer } from './state/client-info/client-info.reducer';
import { DashboardContentEffects } from './state/dashboard-content/dashboard-content.effects';
import { dashboardContentReducer } from './state/dashboard-content/dashboard-content.reducer';
import { SlideshowContentEffects } from './state/slideshow-content/slideshow-content.effects';
import { slideshowContentReducer } from './state/slideshow-content/slideshow-content.reducer';
import { userActions } from './state/user/user.actions';
import { UserEffects } from './state/user/user.effects';
import { IUserState } from './state/user/user.interface';
import { userReducer } from './state/user/user.reducer';

export type TReducerState = {
  [A in keyof typeof reducers]: (typeof reducers)[A] extends ActionReducer<infer X> ? X : never;
};

const reducers = {
  clientInfo: clientInfoReducer,
  dashboardContent: dashboardContentReducer,
  slideshowContent: slideshowContentReducer,
  user: userReducer,
};

const customRouteReuseStrategy: RouteReuseStrategy = {
  retrieve(): DetachedRouteHandle | null {
    return null;
  },
  shouldAttach(): boolean {
    return false;
  },
  shouldDetach(): boolean {
    return false;
  },
  shouldReuseRoute(): boolean {
    return false;
  },
  store(): void {
    return;
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
    ),
    { provide: RouteReuseStrategy, useValue: customRouteReuseStrategy },
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([loadingInterceptor, screenLockInterceptor]), withInterceptorsFromDi()),
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          deps: [HttpClient],
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
        },
      }),
      NgxEditorModule.forRoot(),
      ServiceWorkerModule.register('scw-db-sw.js', { enabled: environment.production }),
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideStore(reducers),
    provideEffects([UserEffects, ClientInfoEffects, DashboardContentEffects, SlideshowContentEffects]),
    provideAppInitializer(() => appInitializerFactory(inject(TranslateService), inject(Store))()),
    { provide: MatPaginatorIntl, useFactory: () => new CustomMatPaginatorIntl(inject(TranslateService)) },
    {
      deps: [TranslateService],
      provide: NGX_EDITOR_CONFIG_TOKEN,
      useFactory: TextEditorComponent.ngxEditorConfigFactory,
    },
    provideMomentDateAdapter(),
    {
      deps: [Store<TReducerState>, Router],
      provide: WIDGET_GLOBAL_DATA,
      useFactory: (store: Store<TReducerState>, router: Router): IWidgetGlobalData => {
        const advancedFilterManager$ = new Observable().pipe(
          startWith(null),
          map(() => {
            let child = router.routerState.snapshot.root;

            while (child.firstChild) {
              child = child.firstChild;
            }

            return child.data?.['advancedFilterManager'] as AdvancedFilterManager | undefined;
          }),
          filter(Boolean),
        );
        const user = store.selectSignal(({ user: userStore }) => userStore.user!);
        const colors = store.selectSignal(
          ({ dashboardContent }) => dashboardContent.dashboardData?.workspace.configuration?.colors.activity,
        );

        return {
          actionTrackerHttpContext: actionTrackerContext,
          actionTrackerUrl: user().moduleUrls.actionTrackerFrontendUrl,
          activityColor: colors,
          appliedGlobalFilters: advancedFilterManager$.pipe(map(({ appliedFilters }) => appliedFilters)),
          dateFormat: user().dfUserConfiguration.dateFormat,
          dateIsSetManually: advancedFilterManager$.pipe(
            switchMap(({ dateIsSetManuallySubject }) => dateIsSetManuallySubject),
          ),
          decimalScaleLimit: user().dfUserConfiguration.decimalScaleLimit,
          decimalSeparator: user().dfUserConfiguration.decimalSeparator,
          env: environment.env,
          isProduction: environment.production,
          language: user().dfUserConfiguration.language,
          locale: user().dfUserConfiguration.locale,
          oeeBaseType: user().dfUserConfiguration.virtualFlags?.oeeBaseType,
          oeeCalculationBasis: user().dfUserConfiguration.virtualFlags?.oeeCalculationBasis,
          oeeTrackerHttpContext: oeeContext,
          sqcdpDate: advancedFilterManager$.pipe(map(({ sqcdpDate }) => sqcdpDate)),
          thousandSeparator: user().dfUserConfiguration.thousandSeparator,
          timezone: user().dfUserConfiguration.timezone,
        };
      },
    },
    UsersStore,
  ],
};

function httpLoaderFactory(httpClient: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', `.json?v=${environment.version}`);
}

function appInitializerFactory(translate: TranslateService, store: Store<TReducerState>): () => Promise<void> {
  return () =>
    new Promise<void>((resolve: (value: void) => void, reject: (value: void) => void): void => {
      translate.setDefaultLang('en');
      store.dispatch(clientInfoActions.getClientInfo());
      store.dispatch(userActions.getCurrentUser());

      store
        .select((state: TReducerState) => state.user)
        .pipe(
          filter((state: IUserState): boolean => state.user !== null),
          take(1),
        )
        .subscribe((state: IUserState): void => {
          const user = state.user!;
          const langToSet = user.dfUserConfiguration.language;
          translate
            .use(langToSet)
            .pipe(
              filter((translateResponseData): boolean => translateResponseData !== undefined),
              take(1),
            )
            .subscribe({
              complete: (): void => {
                document.getElementById('splash-screen')?.remove();
                resolve();
              },
              error: (): void => {
                reject();
              },
            });
        });
    });
}
